import { get, isNumber, first, isEqual, isEmpty, has } from 'lodash';

import scale from 'utils/scale-value';
import { isMapType } from 'constants/chart-roles';
import { formatNumber } from 'utils/number-formats';
import { attemptCast } from 'utils/bson';
import { DEFAULT_RADIUS_RANGE, GLOBE_BOUNDS } from 'constants/geospatial';
import GeoSpatialChoroplethRole from 'config/map-types/geospatial-choropleth';

const isValidNumber = n => !isNaN(n) && isNumber(n);

/**
 * Given an array of objects, find the minimum and maximum for each field given
 *
 * @param {Array} arr An array containing objects
 * @param {...String} fields Fields to use from each object in the array when finding the min and max
 * @return {Array} - An array containing arrays containing each min and max in the form [min, max]
 */
function findMinMax(arr, ...fields) {
  const fieldCache = {};

  for (const field of fields) {
    fieldCache[field] = {};
    fieldCache[field].min = get(arr[0], field);
    fieldCache[field].max = get(arr[0], field);
  }

  for (const n of arr) {
    for (const field of fields) {
      const v = get(n, field);

      if (v < fieldCache[field].min) {
        fieldCache[field].min = v;
      }

      if (v > fieldCache[field].max) {
        fieldCache[field].max = v;
      }
    }
  }

  return Object.values(fieldCache).map(val => [val.min, val.max]);
}

function getGeopointProps(doc, metadata, spec, sizeRange) {
  const props = {
    center: doc.geopoint.coordinates,
    color: metadata.colorMapping[doc.color],
    fields: [
      {
        key: 'Latitude',
        value: doc.geopoint.coordinates[0],
      },
      {
        key: 'Longitude',
        value: doc.geopoint.coordinates[1],
      },
    ],
  };

  const specFields = {
    opacity: value => {
      props.fillOpacity = value;
    },
    blur: value => {
      props.blur = value;
    },
    strokeThickness: value => {
      props.weight = value;
    },
  };

  Object.keys(specFields).forEach(key => {
    const value = spec[key];

    if (value !== undefined) {
      specFields[key](value);
    }
  });

  if (spec.titles && spec.titles.color) {
    props.fields.push({
      key: spec.titles.color,
      value: doc.color,
    });
  }

  if (spec.titles && spec.titles.size && isValidNumber(doc.size)) {
    const scaled = scale(doc.size, sizeRange, DEFAULT_RADIUS_RANGE);

    props.fields.push({
      key: spec.titles.size,
      value: formatNumber(doc.size, spec.customisations.size),
    });

    props.radius = scaled;
  }

  if (isValidNumber(doc.intensity)) {
    props.intensity = doc.intensity;
  }

  return props;
}

function generateMapProps(documents, spec) {
  const mapProps = {};

  if (!isEmpty(spec.viewport) && !spec.autoViewport) {
    mapProps.viewport = spec.viewport;
  } else {
    const coordinates = documents.map(doc => doc.geopoint.coordinates);

    // Handle edge case where coordinate only contain two of the same coordinates
    if (coordinates.length === 2 && isEqual(coordinates[0], coordinates[1])) {
      coordinates.pop();
    }

    if (coordinates.length > 1) {
      mapProps.bounds = coordinates;
    } else if (coordinates.length < 1) {
      // If no coordinates, set bounds as the whole globe
      mapProps.bounds = GLOBE_BOUNDS;
    }

    // Only set center if we have coordinates
    if (coordinates.length) {
      mapProps.center = first(coordinates);
    }
  }

  return mapProps;
}

/**
 * If the chart type is a map type, we need to normalise the data so that it is of the form:
 *
 * [
 *   {
 *     type: 'Point',
 *     coordinates: [lat, lon]
 *     ...other props
 *   },
 *   {
 *     type: 'Point',
 *     coordinates: [lat, lon]
 *     ...other props
 *   },
 * ]
 *
 * That is, it is an array, containing GeoJSON point objects
 *
 * @param  {Object} stageData  Object containing documents returned from the agg pipeline, as well as metadata about the chart type
 * @return {Object}            Object containing documents with the values simplified to GeoJSON points
 */
function normaliseCoordinates(stageData) {
  const {
    documents,
    metadata,
    spec,
    environment: { chartType },
  } = stageData;

  if (!isMapType(chartType) || chartType === GeoSpatialChoroplethRole.name) {
    return {};
  }

  // Cast doc size and intensity
  documents.forEach(doc => {
    if (doc.size) {
      doc.size = attemptCast(doc.size);
    }

    if (doc.intensity) {
      doc.intensity = attemptCast(doc.intensity);
    }
  });

  const [sizeRange] = findMinMax(documents, 'size');

  const normalisedDocuments = documents.reduce((acc, doc) => {
    if (has(doc, 'geopoint.coordinates')) {
      const [lon, lat] = get(doc, 'geopoint.coordinates');
      const newDoc = {
        ...doc,
        geopoint: {
          ...doc.geopoint,
          coordinates: [lat, lon], // Leaflet expects [lat,lon]
        },
      };
      Object.assign(
        newDoc.geopoint,
        getGeopointProps(newDoc, metadata, spec, sizeRange)
      );
      acc.push(newDoc);
    }
    return acc;
  }, []);

  const mapProps = generateMapProps(normalisedDocuments, spec);

  return {
    documents: normalisedDocuments,
    spec: {
      ...stageData.spec,
      ...mapProps,
    },
  };
}

export default {
  input: [
    'spec',
    'documents',
    'environment.chartType',
    'metadata.colorMapping',
  ],
  fn: normaliseCoordinates,
};
export { normaliseCoordinates, findMinMax, DEFAULT_RADIUS_RANGE };
